import { useEffect, useState } from "react";
import type { AdminUserRow, AssistantMode, AuthUser } from "../types";
import { MODEL_OPTIONS, AGENT_LABEL, type AgentKind } from "../models";
import { MODE_LABEL } from "./ModeSelector";

const MODES: AssistantMode[] = ["business", "technical", "support", "executive", "teaching", "concise", "structured"];
const AGENTS: AgentKind[] = ["claude", "codex", "gemini"];

interface Props {
  user: AdminUserRow;
  onUpdated: (user: AdminUserRow) => void;
}

/** Badge selecionável tipo aba: marcado = ativo (preenchido), desmarcado = inativo (contorno). */
function ToggleBadge({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean;
  onClick: () => void;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={`perm-badge ${active ? "active" : ""}`}
      aria-pressed={active}
      title={title}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/** Edita quais motores, modos e modelos esta pessoa tem permissão de usar. */
export function UserPermissionsForm({ user, onUpdated }: Props) {
  const [agents, setAgents] = useState<AgentKind[]>(user.allowedAgents);
  const [modes, setModes] = useState<AssistantMode[]>(user.allowedModes);
  const [models, setModels] = useState<string[]>(user.allowedModels);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // troca de usuário selecionado -> recarrega o form com os dados dessa pessoa
  useEffect(() => {
    setAgents(user.allowedAgents);
    setModes(user.allowedModes);
    setModels(user.allowedModels);
    setError(null);
  }, [user.id, user.allowedAgents, user.allowedModes, user.allowedModels]);

  const dirty =
    agents.length !== user.allowedAgents.length ||
    modes.length !== user.allowedModes.length ||
    models.length !== user.allowedModels.length ||
    agents.some((a) => !user.allowedAgents.includes(a)) ||
    modes.some((m) => !user.allowedModes.includes(m)) ||
    models.some((m) => !user.allowedModels.includes(m));

  const toggleAgent = (a: AgentKind) => {
    setAgents((prev) => {
      const has = prev.includes(a);
      if (has && prev.length === 1) return prev; // sempre pelo menos 1 motor
      return has ? prev.filter((x) => x !== a) : [...prev, a];
    });
  };
  const toggleMode = (m: AssistantMode) => {
    setModes((prev) => {
      const has = prev.includes(m);
      if (has && prev.length === 1) return prev; // sempre pelo menos 1 modo
      return has ? prev.filter((x) => x !== m) : [...prev, m];
    });
  };
  const toggleModel = (id: string) => {
    setModels((prev) => {
      const has = prev.includes(id);
      if (has && prev.length === 1) return prev; // sempre pelo menos 1 modelo
      return has ? prev.filter((x) => x !== id) : [...prev, id];
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const r = await fetch(`/api/admin/users/${encodeURIComponent(user.id)}/permissions`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ allowedAgents: agents, allowedModes: modes, allowedModels: models }),
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? "Falha ao salvar permissões.");
      }
      // a resposta é o PublicUser (sem webhookUrl/sessionCount/etc.) - mescla
      // por cima da linha atual, que já tem os campos só-de-admin.
      const j = (await r.json()) as { user: AuthUser };
      onUpdated({ ...user, ...j.user });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao salvar permissões.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-block">
      <h3>Permissões (motor, modos e modelos)</h3>
      <p className="admin-user-meta" style={{ marginBottom: 10 }}>
        Controla o que esta pessoa pode escolher no chat. Clique pra ativar/desativar - sempre precisa
        sobrar pelo menos uma opção marcada em cada grupo.
      </p>

      <div style={{ marginBottom: 12 }}>
        <span className="admin-user-meta" style={{ display: "block", marginBottom: 6 }}>Motores permitidos</span>
        <div className="perm-badge-row">
          {AGENTS.map((a) => (
            <ToggleBadge
              key={a}
              active={agents.includes(a)}
              onClick={() => toggleAgent(a)}
              title={a !== "claude" ? `Só funciona se a instância tiver credenciais do ${AGENT_LABEL[a]} configuradas` : undefined}
            >
              {AGENT_LABEL[a]}
            </ToggleBadge>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <span className="admin-user-meta" style={{ display: "block", marginBottom: 6 }}>Modos permitidos</span>
        <div className="perm-badge-row">
          {MODES.map((m) => (
            <ToggleBadge key={m} active={modes.includes(m)} onClick={() => toggleMode(m)}>
              {MODE_LABEL[m]}
            </ToggleBadge>
          ))}
        </div>
      </div>

      {AGENTS.filter((a) => agents.includes(a)).map((a) => (
        <div key={a} style={{ marginBottom: 12 }}>
          <span className="admin-user-meta" style={{ display: "block", marginBottom: 6 }}>
            Modelos permitidos — {AGENT_LABEL[a]}
          </span>
          <div className="perm-badge-row">
            {MODEL_OPTIONS.filter((opt) => opt.agent === a).map((opt) => (
              <ToggleBadge
                key={`${opt.agent}:${opt.id || "default"}`}
                active={models.includes(opt.id)}
                onClick={() => toggleModel(opt.id)}
                title={opt.hint}
              >
                {opt.label}
              </ToggleBadge>
            ))}
          </div>
        </div>
      ))}

      {error && <div className="auth-expired" style={{ marginBottom: 8 }}>{error}</div>}

      <button className="btn-approve" type="button" disabled={!dirty || saving} onClick={() => void save()}>
        {saving ? "Salvando…" : "Salvar permissões"}
      </button>
    </div>
  );
}
